import parser from "iptv-playlist-parser";
import { decodeEntities } from "./entities.js";
import { hashId, slugify } from "./hash.js";
import { extractYear } from "./title.js";
const vodGroupPattern = /(?:^|\b)(?:movie|movies|vod|film|films)(?:$|\b)/i;
const seriesGroupPattern = /(?:^|\b)(?:series|tv[- ]?shows?|shows?)(?:$|\b)/i;
/** A film file, or a panel-style /movie/ path. */
const vodFilePattern = /\.(?:mp4|mkv|avi|mov|webm)(?:[?#]|$)|\/movie\//i;
const episodePattern = /\bS(\d{1,2})E(\d{1,3})\b/i;
export function parseM3U(raw) {
    return parseM3UPlaylist(raw).entries;
}
export function parseM3UPlaylist(raw) {
    // The upstream parser expects #EXTM3U to be the first characters. Normalize
    // the UTF-8 BOM while leaving CRLF and all metadata untouched.
    const normalized = raw.replace(/^\uFEFF/, "");
    // Some hand-made playlists start straight at their first #EXTINF with no
    // #EXTM3U line; players accept them, so this does too.
    const headed = /^\s*#EXTINF/i.test(normalized) ? `#EXTM3U\n${normalized.trimStart()}` : normalized;
    const parsed = parser.parse(headed);
    const headerAttrs = parsed.header.attrs;
    const epgUrl = headerAttrs["x-tvg-url"] ?? headerAttrs["url-tvg"];
    // The parser reads only the guide address from the header, so its shift is read here.
    const headerShift = /\btvg-shift="?([^"\s]*)/i.exec(parsed.header.raw ?? "")?.[1]?.trim() || undefined;
    return {
        ...(epgUrl ? { epgUrl } : {}),
        entries: parsed.items
            .filter((item) => item.url.trim().length > 0)
            .map((item) => ({
            // Names and groups decoded once here ("&#127875;", "&amp;"): see entities.ts.
            name: decodeEntities(item.name.trim()),
            ...(item.tvg.logo ? { logoUrl: item.tvg.logo } : {}),
            ...(item.group.title ? { groupTitle: decodeEntities(item.group.title.trim()) } : {}),
            ...(item.tvg.id ? { tvgId: item.tvg.id.trim() } : {}),
            ...(item.tvg.name ? { tvgName: decodeEntities(item.tvg.name.trim()) } : {}),
            streamUrl: item.url.trim(),
            ...(item.catchup.type ? { catchupType: item.catchup.type } : {}),
            ...(item.catchup.days ? { catchupDays: item.catchup.days } : {}),
            ...(item.catchup.source ? { catchupSource: item.catchup.source } : {}),
            ...(item.timeshift ? { timeshift: item.timeshift } : {}),
            ...(item.tvg.shift?.trim() || headerShift ? { tvgShift: item.tvg.shift?.trim() || headerShift } : {}),
            ...(item.lang ? { language: item.lang } : {}),
            ...(item.http.referrer ? { referrer: item.http.referrer } : {}),
            ...(item.http["user-agent"] ? { userAgent: item.http["user-agent"] } : {}),
            raw: item.raw,
        }))
    };
}
/**
 * What a group holds, judged by its ENTRIES first and its name only to break
 * a tie. The name alone used to decide, and public playlists name live-channel
 * groups "Movies" and "Series": iptv-org's UK list (2026-09-18) filed 18 live
 * film channels as movies and 47 live channels as one-episode series.
 */
export function classifyM3UGroup(entries) {
    const groupTitle = entries.find((entry) => entry.groupTitle)?.groupTitle ?? "";
    const files = entries.filter((entry) => vodFilePattern.test(entry.streamUrl)).length;
    const episodes = entries.filter((entry) => episodePattern.test(entry.name) || /\/series\//i.test(entry.streamUrl)).length;
    // Streams with no sign of being a file or an episode are channels, whatever the group is called.
    if (files === 0 && episodes === 0)
        return "live";
    if (episodes * 2 >= entries.length)
        return "series";
    if (files === entries.length)
        return "vod";
    // A mix: the name decides, and without one it stays with the channels.
    if (seriesGroupPattern.test(groupTitle) && episodes > 0)
        return "series";
    if (vodGroupPattern.test(groupTitle))
        return "vod";
    return "uncategorized";
}
/** Normalize entries as channels; use normalizeM3UCatalog for heuristic VOD/series splitting. */
export function normalizeM3UEntries(entries, sourceId) {
    return entries.map((entry, index) => normalizeChannel(entry, sourceId, index));
}
export function normalizeM3UCatalog(playlist, sourceId) {
    const groups = new Map();
    for (const entry of playlist.entries) {
        const key = entry.groupTitle?.trim() || "uncategorized";
        groups.set(key, [...(groups.get(key) ?? []), entry]);
    }
    const channels = [];
    const vod = [];
    const series = [];
    for (const entries of groups.values()) {
        const kind = classifyM3UGroup(entries);
        if (kind === "live" || kind === "uncategorized") {
            channels.push(...entries.map((entry, index) => normalizeChannel(entry, sourceId, index)));
        }
        else if (kind === "vod") {
            vod.push(...entries.map((entry) => normalizeVod(entry, sourceId)));
        }
        else {
            series.push(...normalizeSeries(entries, sourceId));
        }
    }
    return {
        channels,
        vod,
        series,
        ...(playlist.epgUrl ? { epgUrl: playlist.epgUrl } : {}),
    };
}
/**
 * `tvg-shift` in minutes: hours as the playlist gives them, whole or in parts
 * ("+2", "-1.5", "5.75"), within a day either way. Nothing for 0 or anything
 * that is not a number.
 */
export function tvgShiftMinutes(value) {
    if (!value)
        return undefined;
    const hours = Number(value.trim().replace(/^\+/, "").replace(",", "."));
    if (!Number.isFinite(hours) || Math.abs(hours) > 24)
        return undefined;
    const minutes = Math.round(hours * 60);
    return minutes === 0 ? undefined : minutes;
}
function normalizeChannel(entry, sourceId, index) {
    // Catch-up used to be parsed and then thrown away here, so no M3U source could ever
    // advertise replay. Carry the provider's OWN declaration through: the template it
    // published (`catchup-source`) plus how it applies, and the depth it claimed.
    const days = entry.catchupDays === undefined ? undefined : Number(entry.catchupDays);
    const archiveDays = days !== undefined && Number.isFinite(days) && days > 0 ? days : undefined;
    const declaresArchive = Boolean(entry.catchupSource) || archiveDays !== undefined;
    const shift = tvgShiftMinutes(entry.tvgShift);
    return {
        id: hashId(sourceId, entry.tvgId ?? entry.streamUrl),
        sourceId,
        name: entry.name || entry.tvgName || `Channel ${index + 1}`,
        ...(entry.logoUrl ? { logoUrl: entry.logoUrl } : {}),
        streamUrl: entry.streamUrl,
        categoryId: slugify(entry.groupTitle ?? "uncategorized"),
        ...(entry.tvgId ? { epgChannelId: entry.tvgId } : {}),
        ...(shift !== undefined ? { epgShiftMinutes: shift } : {}),
        ...(declaresArchive ? { catchupAvailable: true } : {}),
        ...(archiveDays !== undefined ? { catchupDays: archiveDays } : {}),
        ...(entry.catchupSource ? { catchupSource: entry.catchupSource } : {}),
        ...(entry.catchupType ? { catchupType: entry.catchupType } : {}),
        hidden: false,
        health: "unknown",
    };
}
function normalizeVod(entry, sourceId) {
    const year = extractYear(entry.name);
    return {
        id: hashId(sourceId, entry.streamUrl),
        sourceId,
        rawTitle: entry.name || entry.tvgName || "Untitled",
        streamUrl: entry.streamUrl,
        categoryId: slugify(entry.groupTitle ?? "uncategorized"),
        ...(entry.logoUrl ? { posterUrl: entry.logoUrl } : {}),
        ...(year ? { year } : {}),
    };
}
function normalizeSeries(entries, sourceId) {
    const byTitle = new Map();
    for (const entry of entries) {
        const title = (entry.name.match(new RegExp(`^(.*?)\\s*${episodePattern.source}`, "i"))?.[1] ?? entry.name)
            .replace(/[._]+/g, " ")
            .replace(/\s{2,}/g, " ")
            .trim() || "Untitled";
        const key = title.toLowerCase();
        const existing = byTitle.get(key);
        if (existing)
            existing.entries.push(entry);
        else
            byTitle.set(key, { title, entries: [entry] });
    }
    return [...byTitle.values()].map(({ title, entries: groupedEntries }) => {
        const first = groupedEntries[0];
        const seriesId = hashId(sourceId, title);
        const episodes = groupedEntries.map((entry, index) => {
            const match = entry.name.match(episodePattern);
            return {
                id: hashId(sourceId, entry.streamUrl),
                seriesId,
                seasonNumber: match?.[1] ? Number(match[1]) : 1,
                episodeNumber: match?.[2] ? Number(match[2]) : index + 1,
                title: entry.name,
                streamUrl: entry.streamUrl,
            };
        });
        return {
            id: seriesId,
            sourceId,
            rawTitle: title,
            categoryId: slugify(first?.groupTitle ?? "series"),
            seasons: groupEpisodes(episodes),
        };
    });
}
function groupEpisodes(episodes) {
    const seasons = new Map();
    for (const episode of episodes) {
        seasons.set(episode.seasonNumber, [...(seasons.get(episode.seasonNumber) ?? []), episode]);
    }
    return [...seasons.entries()]
        .sort(([a], [b]) => a - b)
        .map(([seasonNumber, seasonEpisodes]) => ({ seasonNumber, episodes: seasonEpisodes }));
}
//# sourceMappingURL=m3u.js.map